<div class="page">
    <app-page-header [title]="'races.title' | translate" [subtitle]="'races.subtitle' | translate">
      <select (change)="onSeasonChange($any($event.target).value)"
        [attr.aria-label]="'courses.season' | translate" class="field w-full sm:w-auto">
        <option value="all" [selected]="seasonFilter() === null">{{ 'common.allSeasons' | translate }}</option>
        @for (season of availableSeasons(); track season) {
          <option [value]="season" [selected]="seasonFilter() === season">{{ 'courses.season' | translate }} {{ season }}–{{ season + 1 }}</option>
        }
      </select>

      <select [value]="federationRegionFilter() || ''" (change)="onFederationRegionChange($any($event.target).value)"
        [attr.aria-label]="'races.allFfslcRegions' | translate" class="field w-full sm:w-auto">
        <option value="">{{ 'races.allFfslcRegions' | translate }}</option>
        @for (federation of ffslcRegions; track federation.code) {
          <option [value]="federation.code">{{ federation.code }} · {{ federation.name }}</option>
        }
      </select>

      @if (availableDepartments().length > 0) {
        <select [value]="departmentFilter() || ''" (change)="onDepartmentChange($any($event.target).value)"
          [attr.aria-label]="'races.allDepartments' | translate" class="field w-full sm:w-auto">
          <option value="">{{ 'races.allDepartments' | translate }}</option>
          @for (department of availableDepartments(); track department) {
            <option [value]="department">{{ formatDepartment(department) }}</option>
          }
        </select>
      }

      <select [value]="regionFilter() || ''" (change)="onRegionClicked($any($event.target).value)"
        [attr.aria-label]="'common.allRegions' | translate" class="field w-full sm:w-auto">
        <option value="">{{ 'common.allRegions' | translate }}</option>
        <option value="Auvergne-Rhône-Alpes">Auvergne-Rhône-Alpes</option>
        <option value="Bourgogne-Franche-Comté">Bourgogne-Franche-Comté</option>
        <option value="Bretagne">Bretagne</option>
        <option value="Centre-Val de Loire">Centre-Val de Loire</option>
        <option value="Corse">Corse</option>
        <option value="Grand Est">Grand Est</option>
        <option value="Hauts-de-France">Hauts-de-France</option>
        <option value="Île-de-France">Île-de-France</option>
        <option value="Normandie">Normandie</option>
        <option value="Nouvelle-Aquitaine">Nouvelle-Aquitaine</option>
        <option value="Occitanie">Occitanie</option>
        <option value="Pays de la Loire">Pays de la Loire</option>
        <option value="Provence-Alpes-Côte d'Azur">PACA</option>
      </select>

      <div class="relative w-full sm:w-72">
        <input type="search" (input)="onSearch($event)" [placeholder]="'races.searchPlaceholder' | translate"
          [attr.aria-label]="'races.searchPlaceholder' | translate" class="field pl-11">
        <svg class="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" fill="none" viewBox="0 0 24 24"
          stroke="currentColor" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>

      <button type="button" (click)="requestUserLocation()" [attr.aria-pressed]="userPosition() !== null"
        class="btn w-full sm:w-auto" [ngClass]="userPosition() ? 'btn-primary' : 'btn-secondary'">
        <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.25" d="M12 21s6-4.35 6-11a6 6 0 10-12 0c0 6.65 6 11 6 11z" />
          <circle cx="12" cy="10" r="2" />
        </svg>
        {{ userPosition() ? ('races.sortByDistance' | translate) : ('races.aroundMe' | translate) }}
      </button>
    </app-page-header>

    <section [attr.aria-label]="'accessibility.leagueColors' | translate"
      class="mb-10 rounded-3xl border border-slate-200/70 bg-white/80 p-3 shadow-xl shadow-slate-200/30 backdrop-blur dark:border-white/10 dark:bg-slate-900/75 dark:shadow-none">
      <div class="grid grid-cols-3 gap-2 lg:grid-cols-9">
        @for (federation of ffslcRegions; track federation.code) {
          <button type="button" (click)="onFederationRegionChange(federation.code)"
            [attr.aria-pressed]="federationRegionFilter() === federation.code"
            [attr.title]="federation.colorName + ' · ' + federation.color"
            [class.region-option-active]="federationRegionFilter() === federation.code"
            class="region-option min-w-0 rounded-2xl px-2 py-3 text-left">
            <span class="mb-2 block h-2.5 w-8 rounded-full" [style.background-color]="federation.color"></span>
            <span class="block text-[10px] font-black tracking-widest">{{ federation.code }}</span>
            <span class="mt-0.5 block truncate text-[9px] font-bold text-slate-500 dark:text-slate-400">{{ federation.shortName }}</span>
          </button>
        }
      </div>
    </section>

    @if (locationError()) {
      <p class="-mt-8 mb-8 text-right text-xs font-bold text-amber-700 dark:text-amber-400" role="status">{{ locationError() }}</p>
    }

    @if (isLoading()) {
      <app-branded-loader [label]="'common.loading' | translate" />
    } @else {
    @if (regionFilter() || federationRegionFilter() || departmentFilter()) {
      <div class="mb-6 flex flex-col gap-3 rounded-3xl border border-slate-200/80 bg-white/85 p-3 shadow-lg shadow-slate-200/30 backdrop-blur dark:border-white/10 dark:bg-slate-900/80 dark:shadow-none sm:flex-row sm:items-center sm:justify-between">
        <button type="button" (click)="goUpTerritoryLevel()"
          class="accent-focus accent-hover-soft inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl px-4 py-2 text-xs font-black uppercase tracking-wider text-accent sm:justify-start">
          <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
            <path d="M19 12H5m6 6-6-6 6-6" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          {{ departmentFilter() ? ('races.backToRegion' | translate) : ('races.backToFrance' | translate) }}
        </button>

        <nav class="flex min-w-0 flex-wrap items-center gap-1.5 px-2 text-[11px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400"
          [attr.aria-label]="'accessibility.territoryNavigation' | translate">
          <button type="button" (click)="showTerritoryOverview()" class="rounded-lg px-2 py-1.5 hover:text-accent">
            {{ 'races.france' | translate }}
          </button>
          <span aria-hidden="true">›</span>
          @if (federationRegionFilter(); as federationCode) {
            @if (departmentFilter()) {
              <button type="button" (click)="goUpTerritoryLevel()" class="rounded-lg px-2 py-1.5 hover:text-accent">
                {{ formatFfslcRegion(federationCode) }}
              </button>
            } @else {
              <span class="px-2 py-1.5 text-slate-900 dark:text-white" aria-current="page">{{ formatFfslcRegion(federationCode) }}</span>
            }
          } @else if (regionFilter(); as region) {
            @if (departmentFilter()) {
              <button type="button" (click)="goUpTerritoryLevel()" class="rounded-lg px-2 py-1.5 hover:text-accent">{{ region }}</button>
            } @else {
              <span class="px-2 py-1.5 text-slate-900 dark:text-white" aria-current="page">{{ region }}</span>
            }
          }
          @if (departmentFilter(); as department) {
            <span aria-hidden="true">›</span>
            <span class="px-2 py-1.5 text-slate-900 dark:text-white" aria-current="page">{{ formatDepartment(department) }}</span>
          }
        </nav>
      </div>
    }

    <!-- Main Content Container with conditional layout -->
    <div class="relative min-h-[600px] flex flex-col lg:flex-row gap-8 transition-all duration-700 ease-in-out">

      <!-- Interactive Map Section -->
      <div #mapSection [class.lg:w-full]="viewMode() === 'MAP_CENTERED'" [class.lg:w-[32%]]="viewMode() === 'SPLIT_VIEW'"
        [class.2xl:w-[30%]]="viewMode() === 'SPLIT_VIEW'"
        class="order-2 relative transition-all duration-700 ease-in-out">

        <div
          class="overflow-hidden rounded-3xl border border-slate-100 bg-white p-4 shadow-2xl dark:border-slate-800 dark:bg-slate-900 lg:sticky lg:top-20">
            <div class="p-2">
              <div class="mb-4 flex items-center justify-between px-2">
                <div>
                  <span class="block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
                    {{ (regionFilter() || federationRegionFilter() ? 'races.geoEvents' : 'races.exploreTerritory') | translate }}
                  </span>
                  @if (regionFilter() || federationRegionFilter()) {
                    <span class="text-sm font-black text-slate-900 dark:text-white">{{ 'races.filteredCount' | translate:mapPoints().length:filteredRaces().length }}</span>
                  } @else {
                    <span class="text-sm font-black text-slate-900 dark:text-white">{{ 'races.chooseRegionDepartment' | translate }}</span>
                  }
                </div>
                @if ((regionFilter() || federationRegionFilter()) && mapPoints().length < filteredRaces().length) {
                  <span class="rounded-full bg-amber-100 px-3 py-1 text-[10px] font-black text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">{{ 'races.locationToComplete' | translate }}</span>
                }
              </div>
              <app-event-map
                [points]="mapPoints()"
                [selectedEventId]="selectedEventId()"
                [userPosition]="userPosition()"
                [showTerritories]="true"
                [activeRegion]="regionFilter()"
                [activeFederationRegion]="federationRegionFilter()"
                [activeDepartment]="departmentFilter()"
                (federationRegionSelected)="onTerritoryFederationRegionSelected($event)"
                (regionSelected)="onTerritoryRegionSelected($event)"
                (departmentSelected)="onTerritoryDepartmentSelected($event)"
                (eventSelected)="onMapEventSelected($event)" />
            </div>

          @if (regionFilter() || federationRegionFilter()) {
          <div
            class="accent-surface px-6 py-4 mt-4 rounded-2xl border flex items-center justify-between animate-fade-in">
            <div>
              <span class="text-[10px] font-black text-accent uppercase tracking-widest block">{{
                'races.mapSidebar.filterActive' | translate }}</span>
              <span class="text-sm font-black text-slate-900 dark:text-white uppercase">
                @if (departmentFilter(); as department) {
                  {{ formatDepartment(department) }}
                } @else if (federationRegionFilter(); as federationCode) {
                  {{ formatFfslcRegion(federationCode) }}
                } @else {
                  {{ regionFilter() }}
                }
              </span>
            </div>
            <button type="button" (click)="showTerritoryOverview()" [attr.aria-label]="'accessibility.returnToFranceMap' | translate"
              class="p-2 hover:bg-black/5 dark:hover:bg-white/10 rounded-lg transition-colors">
              <svg class="w-5 h-5 text-accent" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          }
        </div>
      </div>

      <!-- Races List Section -->
      <div #resultsSection data-testid="results-section"
         [class.lg:w-0]="viewMode() === 'MAP_CENTERED'"
         [class.lg:w-[68%]]="viewMode() === 'SPLIT_VIEW'"
         [class.2xl:w-[70%]]="viewMode() === 'SPLIT_VIEW'"
         [class.opacity-0]="viewMode() === 'MAP_CENTERED'" [class.opacity-100]="viewMode() === 'SPLIT_VIEW'"
         [class.pointer-events-none]="viewMode() === 'MAP_CENTERED'"
         [class.overflow-hidden]="viewMode() === 'MAP_CENTERED'"
         class="order-1 scroll-mt-6 transition-all duration-700 ease-in-out">

        <div class="mb-8 rounded-3xl bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 p-3 shadow-xl shadow-slate-200/40 dark:shadow-none animate-fade-in">
          <span class="block px-3 pt-1 pb-3 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
            {{ 'races.eventViews.title' | translate }}
          </span>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-2" role="tablist" [attr.aria-label]="'accessibility.eventType' | translate">
            @for (view of eventViews; track view.code) {
              <button type="button" role="tab"
                (click)="selectEventView(view.code)"
                [attr.aria-selected]="eventView() === view.code"
                [ngClass]="eventView() === view.code
                  ? view.code === 'upcoming'
                    ? 'bg-sky-500 text-white shadow-lg shadow-sky-500/20'
                    : 'bg-emerald-600 text-white shadow-lg shadow-emerald-500/20'
                  : 'bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'"
                class="flex min-h-14 items-center justify-between gap-3 rounded-2xl px-4 py-3 text-left transition-all">
                <span class="text-xs font-black uppercase tracking-wider">{{ view.labelKey | translate }}</span>
                <span class="min-w-8 rounded-full bg-white/25 px-2 py-1 text-center text-xs font-black">
                  {{ eventViewCounts()[view.code] }}
                </span>
              </button>
            }
          </div>
        </div>

        @if (regionFilter() || federationRegionFilter() || departmentFilter()) {
          <div #firstEventSection class="mb-5 flex scroll-mt-20 items-center justify-between gap-3 rounded-2xl border border-slate-200/80 bg-white/90 p-2.5 shadow-sm dark:border-white/10 dark:bg-slate-900/90 lg:hidden">
            <button type="button" (click)="goUpTerritoryLevel()"
              class="accent-hover-soft inline-flex min-h-11 items-center gap-2 rounded-xl px-3 py-2 text-[11px] font-black uppercase tracking-wider text-accent">
              <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                <path d="M19 12H5m6 6-6-6 6-6" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              {{ departmentFilter() ? ('races.backToRegion' | translate) : ('races.backToFrance' | translate) }}
            </button>
            <span class="rounded-full bg-slate-100 px-3 py-1.5 text-[10px] font-black uppercase tracking-wider text-slate-500 dark:bg-slate-800 dark:text-slate-300">
              {{ 'races.eventsCount' | translate:filteredRaces().length }}
            </span>
          </div>
        }

        <div class="space-y-12">
          @for (group of groupedRaces(); track group.region) {
            <div>
              <div class="flex items-center gap-4 mb-6">
                <h2 class="text-xl font-black text-slate-900 dark:text-white uppercase tracking-wider">{{ group.region }}</h2>
                <div class="flex-1 h-px bg-slate-200 dark:bg-slate-800"></div>
                <span class="accent-soft border text-[10px] font-black uppercase tracking-widest py-1.5 px-3 rounded-full">
                  {{ 'races.eventsCount' | translate:group.events.length }}
                </span>
              </div>
              
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                @for (race of group.events; track race.id) {
                  @let eventState = getEventState(race);
                  <a [routerLink]="['/race-detail', race.id]" [id]="'event-card-' + race.id"
                       (mouseenter)="selectEventOnMap(race.id)" (focus)="selectEventOnMap(race.id)"
                       class="group relative overflow-hidden rounded-3xl shadow-sm hover:shadow-xl border p-7 sm:p-8 transition-all duration-300 hover:-translate-y-1 block cursor-pointer text-current no-underline"
                       [class.accent-ring]="selectedEventId() === race.id"
                       [ngClass]="{
                         'bg-emerald-50/80 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800/60': eventState === 'past',
                         'bg-amber-50/80 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800/60': eventState === 'pending',
                         'bg-sky-50/80 dark:bg-sky-950/30 border-sky-200 dark:border-sky-800/60': eventState === 'upcoming',
                         'shadow-xl -translate-y-1': selectedEventId() === race.id
                       }">
                    <div class="absolute inset-x-0 top-0 h-1"
                      [ngClass]="{
                        'bg-emerald-500': eventState === 'past',
                        'bg-amber-500': eventState === 'pending',
                        'bg-sky-500': eventState === 'upcoming'
                      }"></div>
                    
                    <div class="flex justify-between items-start mb-6">
                      @if (authService.isAdmin()) {
                        <button (click)="deleteEvent($event, race)"
                          class="p-2 bg-red-50 dark:bg-red-500/10 hover:bg-red-100 dark:hover:bg-red-500/20 text-red-500 rounded-lg transition-colors border border-transparent hover:border-red-200 dark:hover:border-red-500/30">
                          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      } @else {
                        <div></div>
                      }
                      
                      <span class="text-[10px] font-black uppercase tracking-widest py-1.5 px-3 rounded-full border"
                        [ngClass]="{
                          'bg-emerald-100 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-300/70 dark:border-emerald-500/30': eventState === 'past',
                          'bg-amber-100 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-300/70 dark:border-amber-500/30': eventState === 'pending',
                          'bg-sky-100 dark:bg-sky-500/10 text-sky-700 dark:text-sky-400 border-sky-300/70 dark:border-sky-500/30': eventState === 'upcoming'
                        }">
                        @if (eventState === 'past') {
                          {{ 'races.mapLegend.past' | translate }}
                        } @else if (eventState === 'pending') {
                          {{ 'races.mapLegend.pending' | translate }}
                        } @else {
                          {{ 'races.mapLegend.upcoming' | translate }}
                        }
                      </span>
                    </div>

                    <h3 class="text-2xl font-black text-slate-900 dark:text-white mb-3 leading-tight tracking-tight">
                      {{ race.name }}
                    </h3>

                    <div class="mb-6 space-y-2 text-slate-500 dark:text-slate-400 text-xs font-bold">
                      <div class="flex items-center gap-2">
                        <svg class="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line>
                        </svg>
                        {{ formatDate(race.date) }}
                      </div>
                      <div class="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                        <svg class="w-4 h-4 shrink-0 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 21s6-4.35 6-11a6 6 0 10-12 0c0 6.65 6 11 6 11z" />
                          <circle cx="12" cy="10" r="2" />
                        </svg>
                        <span>{{ formatLocation(race) }}</span>
                        @if (distanceLabel(race); as distance) {
                          <span class="accent-soft ml-auto rounded-full px-2 py-0.5 text-[10px] font-black">{{ distance }}</span>
                        }
                      </div>
                      @if (race.geoPrecision === 'city_center' || race.geoPrecision === 'postal_code') {
                        <span class="inline-flex rounded-full bg-white/70 px-2 py-1 text-[9px] font-black uppercase tracking-wider text-slate-500 dark:bg-slate-900/50 dark:text-slate-400">{{ 'common.approximatePosition' | translate }}</span>
                      }
                    </div>

                    <div class="border-t border-slate-100 dark:border-slate-800/80 pt-5 flex items-center justify-between">
                      <div class="text-slate-600 dark:text-slate-400 text-xs font-bold flex items-center gap-2">
                        <span class="text-slate-900 dark:text-white font-black text-sm bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-md">
                          {{ race.races?.length || 0 }}
                        </span>
                        {{ 'races.eventCount' | translate:race.races?.length || 0 }}
                      </div>
                      <div class="accent-soft w-8 h-8 rounded-full flex items-center justify-center transition-transform group-hover:scale-110">
                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </a>
                }
              </div>
            </div>
          } @empty {
          <div class="col-span-full py-32 text-center">
            <div
              class="w-24 h-24 bg-slate-50 dark:bg-slate-900 rounded-full flex items-center justify-center mx-auto mb-8">
              <svg class="w-12 h-12 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <h4 class="text-xl font-black text-slate-900 dark:text-white uppercase tracking-widest mb-2">
              {{ 'races.eventViews.empty' | translate }}
            </h4>
            <p class="text-slate-500 dark:text-slate-400 font-medium">
              {{ selectedEventView().labelKey | translate }} · {{ regionFilter() || ('common.allRegions' | translate) }}
            </p>
            <button type="button" (click)="clearFilter()" class="btn btn-primary mt-8">
              {{ 'races.allCalendar' | translate }}
            </button>
          </div>
          }
        </div>
      </div>
    </div>
    }
</div>
